import React, { useMemo, useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useSelector, useDispatch } from 'react-redux';
import { createPortal } from 'react-dom';
import {
    ArrowLeft, Crown, Globe, CheckCircle2, XCircle, Ban,
    Receipt, IndianRupee, CalendarCheck, Copy
} from 'lucide-react';
import StatusChip from '../../../components/statusChip/StatusChip';
import { TabCard } from '../../../components/custom-card/Cards';
import { formatMomentDate } from '../../../utils/dateHelper';
import { Card, Row, Col } from 'react-bootstrap';
import CustomTable from '../../../components/table/CustomTable';
import { fetchPaymentTransactions, downloadPaymentInvoice, fetchUserSubscriptions } from '../../../redux/slice/subscriptionSlice';
import { reverseUserSubscription } from '../../../redux/slice/allUsersSlice';
import { useToast } from '../../../context/TostContext';
import { saveAs } from 'file-saver';
import ConfirmModal from '../../../components/withModal/ConfirmModal';
import AssignPlanModal from '../../../components/modals/AssignPlanModal';
import { ProfileCard } from '../../../components/custom-card/Cards';

const getStatusBg = (status) => {
    const lowerStatus = status?.toLowerCase();
    switch (lowerStatus) {
        case "active":
            return "chip-green";
        case "trialing":
        case "initialized":
            return "chip-blue";
        case "cancelled":
            return "chip-warning";
        case "expired":
            return "chip-secondary";
        case "paid":
            return "chip-green";
        case "revoked":
        case "failed":
            return "chip-red";
        case "pending":
            return "chip-warning";
        default:
            return "custom-status-chip";
    }
};


const getPaymentStatusBg = (status) => {
    switch (status?.toLowerCase()) {
        case "paid":
        case "success":
            return "chip-green";
        case "failed":
            return "chip-red";
        case "pending":
        case "user_dropped":
            return "chip-warning";
        default:
            return "custom-status-chip";
    }
};

const parsePaymentMethodDetails = (details, group) => {
    if (!details) return "-";
    const method = details.payment_method || details;
    if (group === "upi" && method.upi) {
        return `UPI: ${method.upi.upi_id || "N/A"}`;
    }
    if (group === "card" && method.card) {
        const { card_bank_name, card_network, card_type, card_number } = method.card;
        const typeFormatted = card_type ? card_type.replace("_", " ") : "";
        return [card_bank_name, card_network, typeFormatted, card_number ? `ending in ${card_number}` : ""]
            .filter(Boolean)
            .join(" ")
            .toUpperCase();
    }
    return group ? group.toUpperCase() : "-";
};

const renderHeaderContent = (onBack) => (
    <div className="d-flex align-items-center gap-3 w-100">
        <div className="d-flex align-items-center gap-2">
            <button
                onClick={onBack}
                type="button"
                className="border-0 bg-transparent p-0 d-flex align-items-center justify-content-center primary"
            >
                <ArrowLeft size={22} strokeWidth={2.2} />
            </button>
            <h4 className="fw-semibold mb-0" style={{ fontSize: '18px' }}>
                Subscription Details
            </h4>
        </div>
    </div>
);

const SubscriptionDetails = () => {
    const { id } = useParams();
    const dispatch = useDispatch();
    const navigate = useNavigate();
    const toast = useToast();
    const headerPortalTarget = document.getElementById("header-portal-target");

    const [page, setPage] = useState(1);
    const [limit, setLimit] = useState(10);
    const [loadingId, setLoadingId] = useState(null);
    const [showReversePopup, setShowReversePopup] = useState(false);
    const [showAssignPlanModal, setShowAssignPlanModal] = useState(false);

    const {
        userSubscriptions = [],
        paymentTransactions = [],
        totalPaymentTransactions = 0,
        isLoading
    } = useSelector(state => state.subscription || {});

    const subscription = useMemo(() => {
        if (!userSubscriptions || !id) return null;
        return userSubscriptions.find(sub => String(sub.id) === String(id));
    }, [userSubscriptions, id]);

    const profileCardUser = useMemo(() => {
        if (!subscription?.user) return {};
        const { first_name = "", last_name = "", email, mobile_number, profile, created_at } = subscription.user;
        return {
            name: `${first_name} ${last_name}`.trim() || "-",
            email: email,
            role: profile?.profession?.name || "Subscriber",
            mobile_no: mobile_number,
            image: profile?.profile_photo?.url || "",
            date: created_at,
        };
    }, [subscription?.user]);

    const handleCopyToClipboard = (text, label) => {
        if (!text) return;
        navigator.clipboard.writeText(text);
        toast.success(`${label} copied to clipboard!`);
    };

    useEffect(() => {
        if (id) {
            dispatch(fetchUserSubscriptions({ id, page: 1, limit: 10 }));
            dispatch(fetchPaymentTransactions({ page, limit, subscription_id: id }));
        }
    }, [dispatch, id, page, limit]);

    const handleDownloadInvoice = (item) => {
        setLoadingId(item.id);
        dispatch(downloadPaymentInvoice({ payment_transaction_id: item.id }))
            .unwrap()
            .then((data) => {
                if (data?.base64) {
                    const byteCharacters = atob(data.base64);
                    const byteNumbers = new Array(byteCharacters.length);
                    for (let i = 0; i < byteCharacters.length; i++) {
                        byteNumbers[i] = byteCharacters.charCodeAt(i);
                    }
                    const byteArray = new Uint8Array(byteNumbers);
                    const blob = new Blob([byteArray], { type: "application/pdf" });
                    
                    saveAs(blob, data.filename || `invoice-${item.invoice_no ? item.invoice_no.replace(/\//g, "-") : item.id}.pdf`);
                } else {
                    toast.error("Invoice data not available.");
                }
            })
            .catch((err) => {
                toast.error(err?.message || "Failed to download invoice.");
            })
            .finally(() => {
                setLoadingId(null);
            });
    };

    const handleReverseConfirm = () => {
        if (!subscription) return;
        dispatch(reverseUserSubscription({ subscription_id: subscription.id }))
            .unwrap()
            .then((res) => {
                toast.success(res?.message || "Subscription revoked successfully");
                setShowReversePopup(false);
                dispatch(fetchUserSubscriptions({ id, page: 1, limit: 10 }));
            })
            .catch((err) => {
                toast.error(err?.message || "Failed to revoke subscription");
            });
    };


    const columns = [
        {
            header: "Invoice No",
            cell: (_, item) =>
                item.invoice_no ? (
                    <div className="text-nowrap" style={{ fontSize: "13px", fontFamily: "monospace" }}>
                        {item.invoice_no}
                    </div>
                ) : (
                    "-"
                ),
        },
        {
            header: "Start Date",
            cell: (_, item) =>
                item.start_date ? (
                    <div className="d-flex flex-column text-nowrap">
                        <span>{formatMomentDate(item.start_date, "DD MMM YYYY")}</span>
                        <small className="text-muted">{formatMomentDate(item.start_date, "hh:mm A")}</small>
                    </div>
                ) : (
                    "-"
                ),
        },
        {
            header: "End Date",
            cell: (_, item) =>
                item.end_date ? (
                    <div className="d-flex flex-column text-nowrap">
                        <span>{formatMomentDate(item.end_date, "DD MMM YYYY")}</span>
                        <small className="text-muted">{formatMomentDate(item.end_date, "hh:mm A")}</small>
                    </div>
                ) : (
                    "-"
                ),
        },
        {
            header: "Currency",
            cell: (_, item) => (
                <div className="text-nowrap fw-medium">{item.currency || "-"}</div>
            ),
        },
        {
            header: "Base Amount",
            cell: (_, item) => {
                const amount = item.base_amount;
                return amount != null ? (
                    <div className="text-nowrap">
                        {item.currency || "INR"} {parseFloat(amount).toLocaleString("en-IN")}
                    </div>
                ) : (
                    "-"
                );
            },
        },
        {
            header: "GST %",
            cell: (_, item) => {
                const gst = item.gst_percentage;
                return gst != null ? (
                    <div className="text-nowrap">{parseFloat(gst)}%</div>
                ) : (
                    "-"
                );
            },
        },
        {
            header: "Tax Amount",
            cell: (_, item) => {
                const tax = item.tax_amount;
                return tax != null ? (
                    <div className="text-nowrap">
                        {item.currency || "INR"} {parseFloat(tax).toLocaleString("en-IN")}
                    </div>
                ) : (
                    "-"
                );
            },
        },
        {
            header: "Payable Amount",
            cell: (_, item) => {
                const amount = item.payable_amount;
                return amount != null ? (
                    <div className="text-nowrap fw-semibold">
                        {item.currency || "INR"} {parseFloat(amount).toLocaleString("en-IN")}
                    </div>
                ) : (
                    "-"
                );
            },
        },
        {
            header: "Status",
            cell: (_, item) =>
                item.status ? (
                    <StatusChip
                        bg={getPaymentStatusBg(item.status)}
                        pill
                        title={item.status}
                        className="text-capitalize"
                    />
                ) : (
                    "-"
                ),
        },
        {
            header: "Payment ID",
            cell: (_, item) => {
                const id = item.payment_id;
                return id ? (
                    <div className="text-nowrap" style={{ fontSize: "13px", fontFamily: "monospace" }}>
                        {id}
                    </div>
                ) : (
                    "-"
                );
            },
        },
        {
            header: "Payment Method",
            cell: (_, item) => {
                const group = item.payment_group || item.payment_method?.type;
                const details = item.payment_details || item.payment_method;
                return (
                    <div className="d-flex flex-column gap-1 text-nowrap">
                        <span className="text-capitalize fw-medium">{group || "-"}</span>
                        {details && (
                            <small className="text-muted">
                                {parsePaymentMethodDetails(details, group)}
                            </small>
                        )}
                    </div>
                );
            },
        },
        {
            header: "Service Charge",
            cell: (_, item) => {
                const val = item.service_charge;
                return val != null ? (
                    <div className="text-nowrap">
                        {item.currency || "INR"} {parseFloat(val).toLocaleString("en-IN")}
                    </div>
                ) : (
                    "-"
                );
            },
        },
        {
            header: "Service Tax",
            cell: (_, item) => {
                const val = item.service_tax;
                return val != null ? (
                    <div className="text-nowrap">
                        {item.currency || "INR"} {parseFloat(val).toLocaleString("en-IN")}
                    </div>
                ) : (
                    "-"
                );
            },
        },
        {
            header: "Settlement Amount",
            cell: (_, item) => {
                const val = item.settlement_amount;
                return val != null ? (
                    <div className="text-nowrap fw-medium">
                        {item.currency || "INR"} {parseFloat(val).toLocaleString("en-IN")}
                    </div>
                ) : (
                    "-"
                );
            },
        },
        {
            header: "Error Details",
            cell: (_, item) => {
                const err = item.error_details;
                if (!err) return "-";

                const code = err.error_code || err.code || null;
                const desc = err.error_description || err.description || null;
                const reason = err.error_reason || err.reason || null;
                const fullText = typeof err === "string" ? err : JSON.stringify(err, null, 2);

                if (typeof err === "string") {
                    return (
                        <div
                            className="small text-danger"
                            style={{ maxWidth: "220px", wordBreak: "break-word", cursor: "help" }}
                            title={err}
                        >
                            {err}
                        </div>
                    );
                }

                return (
                    <div
                        className="d-flex flex-column gap-1 small text-danger"
                        style={{ maxWidth: "220px", cursor: "help" }}
                        title={fullText}
                    >
                        {code && (
                            <span className="fw-semibold text-nowrap">{code}</span>
                        )}
                        {desc && (
                            <span style={{ wordBreak: "break-word", whiteSpace: "normal" }}>{desc}</span>
                        )}
                        {reason && (
                            <span className="text-muted text-nowrap">Reason: {reason}</span>
                        )}
                        {!code && !desc && !reason && (
                            <span
                                style={{ overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}
                            >
                                {fullText}
                            </span>
                        )}
                    </div>
                );
            },
        },
        {
            header: "Paid At",
            cell: (_, item) =>
                item.paid_at ? (
                    <div className="d-flex flex-column text-nowrap">
                        <span>{formatMomentDate(item.paid_at, "DD MMM YYYY")}</span>
                        <small className="text-muted">{formatMomentDate(item.paid_at, "hh:mm A")}</small>
                    </div>
                ) : (
                    "-"
                ),
        },
        {
            header: "Created At",
            cell: (_, item) =>
                item.created_at ? (
                    <div className="d-flex flex-column text-nowrap">
                        <span>{formatMomentDate(item.created_at, "DD MMM YYYY")}</span>
                        <small className="text-muted">{formatMomentDate(item.created_at, "hh:mm A")}</small>
                    </div>
                ) : (
                    "-"
                ),
        },
        {
            header: "Updated At",
            cell: (_, item) =>
                item.updated_at ? (
                    <div className="d-flex flex-column text-nowrap">
                        <span>{formatMomentDate(item.updated_at, "DD MMM YYYY")}</span>
                        <small className="text-muted">{formatMomentDate(item.updated_at, "hh:mm A")}</small>
                    </div>
                ) : (
                    "-"
                ),
        },
    ];

    const actionList = [
        {
            title: "Download Invoice",
            isVisible: (row) => row.status === 'paid',
            isLoading: (row) => loadingId === row.id,
            handler: (row) => handleDownloadInvoice(row)
        }
    ];

    const handleBack = () => {
        navigate('/subscriptions');
    };

    const getCreatedBy = () => {
        if (!subscription) return "-";
        if (subscription.created_by_type === 'admin' && subscription.creator_admin) {
            return `Admin: ${subscription.creator_admin.name}`;
        }
        if (subscription.created_by_type === 'user' && subscription.creator_user) {
            return `User: ${subscription.creator_user.first_name} ${subscription.creator_user.last_name}`;
        }
        return "-";
    };

    const showQuickActions = subscription?.source === 'admin';

    if (!subscription) {
        return (
            <div className="position-relative h-100">
                {headerPortalTarget && createPortal(renderHeaderContent(handleBack), headerPortalTarget)}
                <div className="border rounded-3 bg-white p-4 text-center">
                    <p className="text-muted mb-0">{isLoading ? "Loading details..." : "Subscription not found."}</p>
                </div>
            </div>
        );
    }

    return (
        <div className="position-relative h-100">
            {headerPortalTarget && createPortal(renderHeaderContent(handleBack), headerPortalTarget)}

            <Row className="gy-4">
                {/* Card 1: User Details */}
                <Col lg={3}>
                    <ProfileCard
                        title="User Details"
                        user={profileCardUser}
                        link={subscription.user?.id ? `/user/user-detail/${subscription.user.id}` : "#"}
                        className="h-100"
                    />
                </Col>

                {/* Card 2: Subscription Overview */}
                <Col lg={showQuickActions ? 6 : 9}>
                    <Card className="rounded-3 p-4 border-0 common-shadow bg-white h-100">
                        <h5 className="fw-bold mb-4" style={{ fontSize: '16px', color: '#444050' }}>Subscription Overview</h5>
                        
                        <div className="d-flex align-items-center gap-3 mb-4">
                            <div
                                className="d-flex align-items-center justify-content-center rounded-3 flex-shrink-0"
                                style={{
                                    width: '48px',
                                    height: '48px',
                                    background: 'linear-gradient(135deg, #fff0f0, #ffe0e0)',
                                }}
                            >
                                <Crown size={22} style={{ color: 'var(--primary, #e74c6f)' }} />
                            </div>
                            <div className="d-flex flex-column">
                                <div className="d-flex align-items-center gap-2 flex-wrap">
                                    <span className="fw-bold" style={{ fontSize: '15px', color: '#444050' }}>
                                        {subscription.plan?.name || "-"}
                                    </span>
                                    {subscription.status && (
                                        <StatusChip
                                            bg={getStatusBg(subscription.status)}
                                            pill
                                            title={subscription.status}
                                            className="text-capitalize"
                                        />
                                    )}
                                </div>
                                <span className="text-muted text-capitalize fs-sm mt-0.5">
                                    {subscription.plan?.interval || "-"} Plan
                                </span>
                            </div>
                        </div>

                        <Row className="gy-0">
                            {/* Left Sub-column */}
                            <Col md={6} className="pe-md-4">
                                <div className="mb-3">
                                    <span className="text-muted small d-block mb-1">Subscription ID</span>
                                    <div className="d-flex align-items-center gap-1.5 fw-medium text-dark" style={{ fontSize: '13px', wordBreak: 'break-all' }}>
                                        <span>{subscription.id}</span>
                                        <button
                                            className="p-0 border-0 bg-transparent text-muted cursor-pointer hover-primary"
                                            onClick={() => handleCopyToClipboard(subscription.id, "Subscription ID")}
                                        >
                                            <Copy size={13} />
                                        </button>
                                    </div>
                                </div>

                                <div className="mb-3">
                                    <span className="text-muted small d-block mb-1">External Subscription ID</span>
                                    <span className="fw-medium text-dark" style={{ fontSize: '13px', wordBreak: 'break-all' }}>
                                        {subscription.external_subscription_id || "-"}
                                    </span>
                                </div>

                                <div className="mb-3">
                                    <span className="text-muted small d-block mb-1">Product ID</span>
                                    <div className="d-flex align-items-center gap-1.5 fw-medium text-dark" style={{ fontSize: '13px', wordBreak: 'break-all' }}>
                                        <span>
                                            {subscription.apple_product_id || subscription.plan?.apple_product_id || "com.rpoint.subscription.mappro.yearly"}
                                        </span>
                                        <button
                                            className="p-0 border-0 bg-transparent text-muted cursor-pointer hover-primary"
                                            onClick={() => handleCopyToClipboard(subscription.apple_product_id || subscription.plan?.apple_product_id || "com.rpoint.subscription.mappro.yearly", "Product ID")}
                                        >
                                            <Copy size={13} />
                                        </button>
                                    </div>
                                </div>

                                <div className="d-flex justify-content-between py-2">
                                    <span className="text-muted small">Source</span>
                                    <div className="d-flex align-items-center gap-1.5 fw-medium text-dark text-capitalize" style={{ fontSize: '13px' }}>
                                        <Globe size={13} className="text-muted" />
                                        <span>{subscription.source || "-"}</span>
                                    </div>
                                </div>

                                <div className="d-flex justify-content-between py-2">
                                    <span className="text-muted small">Auto Renewal</span>
                                    <div className="d-flex align-items-center gap-1.5 fw-medium text-dark" style={{ fontSize: '13px' }}>
                                        {subscription.auto_renewal ? (
                                            <>
                                                <CheckCircle2 size={13} className="text-success" />
                                                <span>Enabled</span>
                                            </>
                                        ) : (
                                            <>
                                                <XCircle size={13} className="text-muted" />
                                                <span>Disabled</span>
                                            </>
                                        )}
                                    </div>
                                </div>


                                <div className="d-flex justify-content-between py-2">
                                    <span className="text-muted small">Purchase Source</span>
                                    <span className="fw-medium text-dark text-capitalize" style={{ fontSize: '13px' }}>
                                        {subscription.apple_product_id || subscription.source === 'apple' || subscription.source === 'ios' ? "App Store" : "Play Store"}
                                    </span>
                                </div>
                            </Col>

                            {/* Right Sub-column */}
                            <Col md={6} className="ps-md-4 border-start" style={{ borderColor: '#f1f1f4' }}>
                                <div className="d-flex justify-content-between py-2">
                                    <span className="text-muted small">Status</span>
                                    {subscription.status ? (
                                        <StatusChip
                                            bg={getStatusBg(subscription.status)}
                                            pill
                                            title={subscription.status}
                                            className="text-capitalize"
                                        />
                                    ) : (
                                        <span className="fw-medium text-dark" style={{ fontSize: '13px' }}>-</span>
                                    )}
                                </div>

                                <div className="d-flex justify-content-between py-2">
                                    <span className="text-muted small">Trial</span>
                                    <div style={{ fontSize: '13px' }}>
                                        <StatusChip
                                            bg={subscription.is_trial ? "chip-blue" : "chip-secondary"}
                                            pill
                                            title={subscription.is_trial ? "Yes" : "No"}
                                        />
                                    </div>
                                </div>

                                <div className="d-flex justify-content-between py-2">
                                    <span className="text-muted small">Start Date</span>
                                    <span className="fw-medium text-dark text-nowrap" style={{ fontSize: '13px' }}>
                                        {subscription.start_date ? formatMomentDate(subscription.start_date, "DD MMM YYYY, hh:mm A") : "-"}
                                    </span>
                                </div>

                                <div className="d-flex justify-content-between py-2">
                                    <span className="text-muted small">End Date</span>
                                    <span className="fw-medium text-dark text-nowrap" style={{ fontSize: '13px' }}>
                                        {subscription.end_date ? formatMomentDate(subscription.end_date, "DD MMM YYYY, hh:mm A") : "-"}
                                    </span>
                                </div>

                                <div className="d-flex justify-content-between py-2">
                                    <span className="text-muted small">Next Renewal On</span>
                                    <span className="fw-medium text-dark text-nowrap" style={{ fontSize: '13px' }}>
                                        {subscription.end_date ? formatMomentDate(subscription.end_date, "DD MMM YYYY, hh:mm A") : "-"}
                                    </span>
                                </div>

                                <div className="d-flex justify-content-between py-2">
                                    <span className="text-muted small">Created By</span>
                                    <span className="fw-medium text-dark text-nowrap" style={{ fontSize: '13px' }}>
                                        {getCreatedBy()}
                                    </span>
                                </div>

                                <div className="d-flex justify-content-between py-2">
                                    <span className="text-muted small">Created At</span>
                                    <span className="fw-medium text-dark text-nowrap" style={{ fontSize: '13px' }}>
                                        {subscription.created_at ? formatMomentDate(subscription.created_at, "DD MMM YYYY, hh:mm A") : "-"}
                                    </span>
                                </div>

                                <div className="d-flex justify-content-between py-2">
                                    <span className="text-muted small">Updated At</span>
                                    <span className="fw-medium text-dark text-nowrap" style={{ fontSize: '13px' }}>
                                        {subscription.updated_at ? formatMomentDate(subscription.updated_at, "DD MMM YYYY, hh:mm A") : "-"}
                                    </span>
                                </div>
                            </Col>
                        </Row>
                    </Card>
                </Col>

                {/* Card 3: Quick Actions */}
                {showQuickActions && (
                    <Col lg={3}>
                        <Card className="rounded-3 p-4 border-0 common-shadow bg-white h-100">
                            <h5 className="fw-bold mb-4" style={{ fontSize: '16px', color: '#444050' }}>Quick Actions</h5>
                            <div className="d-flex flex-column gap-3">
                                <button
                                    className="btn btn-outline-danger w-100 d-flex align-items-center justify-content-center gap-2 py-2.5"
                                    style={{ borderRadius: '6px', fontSize: '13.5px', fontWeight: 500 }}
                                    onClick={() => setShowReversePopup(true)}
                                    disabled={subscription.status !== 'active'}
                                >
                                    <Ban size={16} /> Revoke Subscription
                                </button>
                            </div>
                        </Card>
                    </Col>
                )}
            </Row>

            {/* Payment Summary Cards */}
            <div className="grid gap-3 mt-4">
                <TabCard
                    className="g-col-4"
                    icon={<Receipt size={20} />}
                    iconClassName="icon-info"
                    title="Total Payments"
                    count={subscription.total_payment_transactions != null ? subscription.total_payment_transactions : (subscription.payment_transactions?.length || subscription.total_payments || 0)}
                />
                <TabCard
                    className="g-col-4"
                    icon={<IndianRupee size={20} />}
                    iconClassName="icon-warning"
                    title="Total Amount Paid"
                    count={`₹ ${subscription.total_amount_paid != null ? parseFloat(subscription.total_amount_paid).toLocaleString() : (subscription.payment_transaction?.payable_amount ? parseFloat(subscription.payment_transaction.payable_amount).toLocaleString() : '0')}`}
                />
                <TabCard
                    className="g-col-4"
                    icon={<CalendarCheck size={20} />}
                    iconClassName="icon-success"
                    title="Last Payment"
                    count={subscription.last_payment_date ? formatMomentDate(subscription.last_payment_date, 'DD MMM YYYY') : (subscription.payment_transaction?.paid_at ? formatMomentDate(subscription.payment_transaction.paid_at, 'DD MMM YYYY') : '-')}
                />  
            </div>

            <Card className='mt-4 bg-white'>
                <Card.Header className='bg-transparent primary'>Renewal History</Card.Header>
                <CustomTable
                    columns={columns}
                    data={paymentTransactions}
                    loading={isLoading}
                    actionList={actionList}
                    emptyMessage="No payment transactions found for this subscription."
                    pagination={{
                        currentPage: page,
                        totalPages: Math.ceil((totalPaymentTransactions || 0) / limit) || 1,
                        total: totalPaymentTransactions,
                        onPageChange: (newPage) => setPage(newPage),
                        pageSize: limit,
                        setPageSize: (newLimit) => {
                            setLimit(newLimit);
                            setPage(1);
                        },
                        setCurrentPage: (newPage) => setPage(newPage)
                    }}
                />
            </Card>

            <ConfirmModal
                isOpen={showReversePopup}
                onClose={() => setShowReversePopup(false)}
                title="Revoke Plan"
                message="Are you sure you want to revoke this plan?"
                onConfirm={handleReverseConfirm}
            />

            <AssignPlanModal
                isOpen={showAssignPlanModal}
                onClose={() => {
                    setShowAssignPlanModal(false);
                    if (id) {
                        dispatch(fetchUserSubscriptions({ id, page: 1, limit: 10 }));
                        dispatch(fetchPaymentTransactions({ page: 1, limit, subscription_id: id }));
                    }
                }}
                userId={subscription.user?.id}
                title="Assign Plan"
            />
        </div>
    );
};

export default SubscriptionDetails;
